import { Component } from "@theme/component";

/**
 * Announcement banner custom element that allows fading between content.
 * On mobile, cycles through announcements. On desktop, shows all at once.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * Media query for mobile detection.
   * @type {MediaQueryList|undefined}
   */
  #mobileMediaQuery = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.#mobileMediaQuery = window.matchMedia("(max-width: 749px)");

    this.addEventListener("mouseenter", this.suspend);
    this.addEventListener("mouseleave", this.resume);
    document.addEventListener("visibilitychange", this.#handleVisibilityChange);

    // Listen for viewport changes to start/stop rotation
    this.#mobileMediaQuery.addEventListener(
      "change",
      this.#handleMediaQueryChange,
    );

    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#mobileMediaQuery?.removeEventListener(
      "change",
      this.#handleMediaQueryChange,
    );
  }

  /**
   * Check if we're on mobile viewport.
   */
  get isMobile() {
    return this.#mobileMediaQuery?.matches ?? false;
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * Only plays on mobile viewports.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay || !this.isMobile) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(":hover") || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute("paused");
  }

  set paused(paused) {
    this.toggleAttribute("paused", paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute("autoplay");
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    // On mobile, hide all slides except the current one
    // On desktop, show all slides
    this.refs.slides?.forEach((slide, index) => {
      if (this.isMobile) {
        slide.setAttribute("aria-hidden", `${index !== relativeIndex}`);
      } else {
        slide.setAttribute("aria-hidden", "false");
      }
    });
  }

  /**
   * Handle viewport changes between mobile and desktop.
   */
  #handleMediaQueryChange = () => {
    if (this.isMobile) {
      // Switched to mobile: start rotation
      this.play();
    } else {
      // Switched to desktop: stop rotation and show all items
      this.suspend();
      this.refs.slides?.forEach((slide) => {
        slide.setAttribute("aria-hidden", "false");
      });
    }
  };

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () =>
    document.hidden ? this.pause() : this.resume();
}

if (!customElements.get("announcement-bar-component")) {
  customElements.define("announcement-bar-component", AnnouncementBar);
}
